<script setup lang="ts">
  import type { HTMLAttributes } from 'vue'
  import { cn } from '@rapoo/element/lib/utils'

  type CSSSize = number | string
  type SkeletonAnimationType = 'pulse' | 'shimmer' | 'none'

  interface Props {
    class?: HTMLAttributes['class']
    height?: CSSSize
    animationType?: SkeletonAnimationType
  }

  const props = withDefaults(defineProps<Props>(), {
    height: '100%',
    animationType: 'none',
  })

  defineSlots<{
    default?: () => unknown
  }>()
</script>

<template>
  <div
    :class="
      cn(
        'pointer-events-none relative block bg-neutral-light-default overflow-hidden',
        {
          'animate-pulse motion-reduce:animate-none': props.animationType === 'pulse',
          'skeleton-shimmer motion-reduce:after:animate-none': props.animationType === 'shimmer',
        },
        props.class,
      )
    "
    aria-hidden="true"
  >
    <slot />
  </div>
</template>

<style scoped>
  .skeleton-shimmer::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
      to bottom in oklab,
      transparent 0%,
      rgb(0 0 0 / 0.1) 50%,
      transparent 100%
    );
    background-size: 100% 200%;
    background-repeat: no-repeat;
    animation: skeleton-shimmer 2s ease-in-out infinite;
  }

  @keyframes skeleton-shimmer {
    0% {
      background-position: 0 200%;
    }
    100% {
      background-position: 0 -100%;
    }
  }
</style>
